import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import moment from 'moment';

import Page from '../../Components/Page';
import PeriodSelect from '../../Components/Utils/PeriodSelect';
import ReactTable from '../../Components/Utils/ReactTable';
import LoadingAnimation from '../../Components/Utils/BateryChargeAnimation';
import IfRender from '../../Components/Utils/IfRender';
import { 
  calculateTotalEnergyConsumption,
  calculateTotalPrice,
  calculateTotalSessions,
  calculateTotalEnergySum
} from '../../Utils/charging-point';

import { selectAuthData } from '../../redux/selectors/auth';
import { organizationReportsFilter } from '../../QueryFilters/queries.js';

import { useTranslation } from 'react-i18next';
import {
  useRequestAllOrganizationsForUserIdQuery,
  useRequestAllOrganizationsQuery
} from '../../redux/api/organizations';
import { selectUserType } from '../../redux/selectors/userType.js';
import './style.scss';

function Reports() {
  const { organizationId: orgId = null, user: { id: userId = '' } = {} } =
    useSelector(selectAuthData);
  const { t } = useTranslation();
  const userType = useSelector(selectUserType);

  const [sessionsInterval, setSessionsInterval] = useState({
    start: moment().startOf('day').toISOString(),
    end: moment().endOf('day').toISOString()
  });
  const [chargers, setChargers] = useState([]);
  const filter = useMemo(
    () => JSON.stringify(organizationReportsFilter(sessionsInterval.start, sessionsInterval.end)),
    [sessionsInterval.start, sessionsInterval.end]
  );

  const {
    isSuccess,
    data: orgData,
    isLoading: orgLoading,
    error
  } = userType?.isSuperAdmin
    ? useRequestAllOrganizationsQuery({
        filter
      })
    : useRequestAllOrganizationsForUserIdQuery({
        id: userId,
        filter
      });

  useEffect(() => {
    const fetchChargingPoints = async () => {
      if (!isSuccess || !orgData) return;
      
      const allChargePoints = Array.isArray(orgData)
        ? orgData.flatMap(org => Array.isArray(org.chargePoints) ? org.chargePoints : [])
        : Array.isArray(orgData.chargePoints) ? orgData.chargePoints : [];
      
      setChargers(allChargePoints);
    };

    fetchChargingPoints();
  }, [isSuccess, orgData]);

  const setTimePeriod = useCallback((start, end) => {
    setSessionsInterval({
      start: start.toISOString(),
      end: end.toISOString()
    });
  }, []);

  // Filter chargers with sessions once
  const chargersWithSessions = useMemo(
    () => chargers.filter((item) => !!item.sessions),
    [chargers]
  );

  // Memoize the total values
  const totalSessionsValue = useMemo(() => calculateTotalSessions(chargers), [chargers]);
  const totalEnergyValue = useMemo(() => calculateTotalEnergyConsumption(chargers), [chargers]);
  const totalPriceValue = useMemo(() => calculateTotalPrice(chargers).toFixed(2), [chargers]);

  const columns = useMemo(
    () => [
      {
        Header: 'Id',
        accessor: 'id',
        Cell: ({ cell }) => (
          <Link
            className='link'
            to={`/reports/sessions?cp=${cell.row.original.id}&from=${moment(
              sessionsInterval.start
            ).unix()}&to=${moment(sessionsInterval.end).unix()}`}>
            {cell.row.original.slug}
          </Link>
        )
      },
      {
        Header: t('cp_name'),
        accessor: 'name'
      },
      {
        Header: t('organization'),
        accessor: 'organization.name'
      },
      {
        Header: t('connectors'),
        accessor: 'chargePointConnectors.length'
      },
      {
        Header: t('sessions'),
        accessor: 'sessions.length'
      },
      {
        Header: t('total_consumption'),
        accessor: 'sessions',
        Cell: ({ cell }) => <>{calculateTotalEnergySum(cell.row?.original?.sessions)}</>
      },
      {
        Header: t('vendor'),
        accessor: 'chargePointConfiguration.vendor'
      }
    ],
    [t, sessionsInterval]
  );

  const isDataReady = !orgLoading && isSuccess && !!orgData && chargers.length > 0;
  const hasError = !!error;

  return (
    <Page title={t('chargers')}>
      <PeriodSelect setDates={setTimePeriod} />
      {hasError && (
        <div className="error-message">
          {t('error_loading_data')}: {error.message || t('unknown_error')}
        </div>
      )}
      <IfRender condition={isDataReady} otherwise={<LoadingAnimation />}>
        <ReactTable data={chargersWithSessions} columns={columns} />
        <div className='total-summary'>
          <p>
            {t('total_sessions')}: <span>{totalSessionsValue}</span>
          </p>

          <p>
            {t('total_consumption')}: <span>{totalEnergyValue}</span>
          </p>
          <p>
            {t('total_cost')}: <span>{totalPriceValue}</span>
          </p>
        </div>
      </IfRender>
    </Page>
  );
}

export default Reports;
